/**
 * Word Bank - Target words for Monster Mode
 * Words categorized by difficulty level for spelling gameplay
 */

/**
 * Word bank entry with difficulty level and visual representation
 */
export interface WordBankEntry {
  word: string;
  difficulty: 'easy' | 'medium' | 'advanced';
  imageUrl: string; // For future image support (MVP uses emoji)
  emoji: string;
}

/**
 * Complete word bank for all difficulty levels
 */
export const WORD_BANK: WordBankEntry[] = [
  // Easy Level: 3-4 letters, common words for ages 3-5
  { word: 'CAT', difficulty: 'easy', imageUrl: '', emoji: '🐱' },
  { word: 'DOG', difficulty: 'easy', imageUrl: '', emoji: '🐕' },
  { word: 'SUN', difficulty: 'easy', imageUrl: '', emoji: '☀️' },
  { word: 'CAR', difficulty: 'easy', imageUrl: '', emoji: '🚗' },
  { word: 'BALL', difficulty: 'easy', imageUrl: '', emoji: '⚽' },
  { word: 'TREE', difficulty: 'easy', imageUrl: '', emoji: '🌳' },
  { word: 'BIRD', difficulty: 'easy', imageUrl: '', emoji: '🐦' },
  { word: 'FISH', difficulty: 'easy', imageUrl: '', emoji: '🐟' },
  { word: 'STAR', difficulty: 'easy', imageUrl: '', emoji: '⭐' },
  { word: 'MOON', difficulty: 'easy', imageUrl: '', emoji: '🌙' },
  { word: 'BOAT', difficulty: 'easy', imageUrl: '', emoji: '⛵' },
  { word: 'BOOK', difficulty: 'easy', imageUrl: '', emoji: '📚' },
  { word: 'BUG', difficulty: 'easy', imageUrl: '', emoji: '🐛' },
  { word: 'BUS', difficulty: 'easy', imageUrl: '', emoji: '🚌' },
  { word: 'HAT', difficulty: 'easy', imageUrl: '', emoji: '🎩' },

  // Medium Level: 4-5 letters, ages 5-7
  { word: 'APPLE', difficulty: 'medium', imageUrl: '', emoji: '🍎' },
  { word: 'HOUSE', difficulty: 'medium', imageUrl: '', emoji: '🏠' },
  { word: 'HAPPY', difficulty: 'medium', imageUrl: '', emoji: '😊' },
  { word: 'TIGER', difficulty: 'medium', imageUrl: '', emoji: '🐯' },
  { word: 'EARTH', difficulty: 'medium', imageUrl: '', emoji: '🌍' },
  { word: 'CLOUD', difficulty: 'medium', imageUrl: '', emoji: '☁️' },
  { word: 'TRAIN', difficulty: 'medium', imageUrl: '', emoji: '🚂' },
  { word: 'PLANT', difficulty: 'medium', imageUrl: '', emoji: '🌱' },
  { word: 'HEART', difficulty: 'medium', imageUrl: '', emoji: '❤️' },
  { word: 'MUSIC', difficulty: 'medium', imageUrl: '', emoji: '🎵' },

  // Advanced Level: 5-7 letters, ages 7+
  { word: 'ROCKET', difficulty: 'advanced', imageUrl: '', emoji: '🚀' },
  { word: 'PLANET', difficulty: 'advanced', imageUrl: '', emoji: '🪐' },
  { word: 'MONSTER', difficulty: 'advanced', imageUrl: '', emoji: '👾' },
  { word: 'RAINBOW', difficulty: 'advanced', imageUrl: '', emoji: '🌈' },
  { word: 'SUNLIGHT', difficulty: 'advanced', imageUrl: '', emoji: '☀️' },
  { word: 'SPACESHIP', difficulty: 'advanced', imageUrl: '', emoji: '🛸' },
  { word: 'GARDEN', difficulty: 'advanced', imageUrl: '', emoji: '🏡' },
  { word: 'TURTLE', difficulty: 'advanced', imageUrl: '', emoji: '🐢' },
];

/**
 * Get all words for a specific difficulty level
 */
export function getWordsByDifficulty(difficulty: 'easy' | 'medium' | 'advanced'): WordBankEntry[] {
  return WORD_BANK.filter(entry => entry.difficulty === difficulty);
}

/**
 * Get a random word for a specific difficulty level
 */
export function getRandomWord(difficulty: 'easy' | 'medium' | 'advanced'): WordBankEntry | null {
  const words = getWordsByDifficulty(difficulty);
  if (words.length === 0) return null;
  return words[Math.floor(Math.random() * words.length)];
}

/**
 * Get a specific word by its text
 */
export function getWordByText(text: string): WordBankEntry | undefined {
  return WORD_BANK.find(entry => entry.word.toUpperCase() === text.toUpperCase());
}

/**
 * Get all available difficulty levels
 */
export function getAvailableDifficulties(): Array<'easy' | 'medium' | 'advanced'> {
  return ['easy', 'medium', 'advanced'];
}

/**
 * Validate word bank completeness
 * Returns object with statistics about the word bank
 */
export function validateWordBank(): {
  totalWords: number;
  easyCount: number;
  mediumCount: number;
  advancedCount: number;
  allHaveEmojis: boolean;
  valid: boolean;
} {
  const easy = getWordsByDifficulty('easy');
  const medium = getWordsByDifficulty('medium');
  const advanced = getWordsByDifficulty('advanced');

  const allHaveEmojis = WORD_BANK.every(entry => entry.emoji.length > 0);

  return {
    totalWords: WORD_BANK.length,
    easyCount: easy.length,
    mediumCount: medium.length,
    advancedCount: advanced.length,
    allHaveEmojis,
    valid: easy.length >= 10 && medium.length >= 8 && advanced.length >= 6 && allHaveEmojis
  };
}

/**
 * Get statistics about the word bank
 */
export function getWordBankStats() {
  const stats = validateWordBank();
  return {
    ...stats,
    averageWordLength: {
      easy: getWordsByDifficulty('easy').reduce((sum, w) => sum + w.word.length, 0) / stats.easyCount,
      medium: getWordsByDifficulty('medium').reduce((sum, w) => sum + w.word.length, 0) / stats.mediumCount,
      advanced: getWordsByDifficulty('advanced').reduce((sum, w) => sum + w.word.length, 0) / stats.advancedCount,
    }
  };
}
